import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import KPICard from '../components/Dashboard/KPICard';
import SalesPerformanceTable from '../components/Dashboard/SalesPerformanceTable';
import UpcomingLaunchesTable from '../components/Dashboard/UpcomingLaunchesTable';
import TimelineSwitcher from '../components/Common/TimelineSwitcher';

const { 
  FiTrendingUp, 
  FiDollarSign, 
  FiPackage, 
  FiUsers,
  FiCalendar,
  FiTarget
} = FiIcons;

const Dashboard = ({ user }) => {
  const [timeline, setTimeline] = useState('MAT');
  const navigate = useNavigate();

  const kpiData = [
    {
      title: 'Total Revenue',
      value: '$247.8M',
      change: '+12.5%',
      changeType: 'positive',
      icon: FiDollarSign,
      description: 'Moving Annual Total',
      color: 'blue'
    },
    {
      title: 'Total Volume',
      value: '156.2M',
      change: '+8.3%',
      changeType: 'positive',
      icon: FiPackage,
      description: 'Units (Eaches)',
      color: 'green'
    },
    {
      title: 'Active Products',
      value: '47',
      change: '+3',
      changeType: 'positive',
      icon: FiTarget,
      description: 'Launched Products',
      color: 'purple'
    },
    {
      title: 'Customer Base',
      value: '1,247',
      change: '+15.2%',
      changeType: 'positive',
      icon: FiUsers,
      description: 'Active Customers',
      color: 'orange'
    }
  ];

  const handleProductClick = (productName) => {
    const productId = productName.toLowerCase().replace(/\s+/g, '-');
    navigate(`/product/${productId}`);
  };

  const handleCustomerClick = (customerName) => {
    const customerId = customerName.toLowerCase().replace(/\s+/g, '-');
    navigate(`/customer/${customerId}`);
  };

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              Welcome back, {user?.name}
            </h1>
            <p className="text-blue-100 text-lg">
              Here's what's happening with your pharmaceutical business today.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <div className="text-center">
              <SafeIcon icon={FiCalendar} className="text-3xl mb-2 mx-auto" />
              <p className="text-sm text-blue-100">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long',
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* KPI Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {kpiData.map((kpi, index) => (
          <KPICard
            key={index}
            {...kpi}
            delay={index * 0.1}
          />
        ))}
      </motion.div>

      {/* Sales Performance Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <SafeIcon icon={FiTrendingUp} className="text-blue-600" />
                Novadoz Product Sales & Volume Performance
              </h2>
              <p className="text-gray-600 mt-1">
                Track performance across all product lines
              </p>
            </div>
            <TimelineSwitcher
              value={timeline}
              onChange={setTimeline}
              options={['MAT', 'YTD', 'Last 30 Days', 'Current Month']}
            />
          </div>
        </div>
        <SalesPerformanceTable 
          timeline={timeline} 
          onProductClick={handleProductClick}
        />
      </motion.div>

      {/* Upcoming Launches Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <SafeIcon icon={FiCalendar} className="text-green-600" />
            Upcoming Novadoz Product Launches
          </h2>
          <p className="text-gray-600 mt-1">
            Pipeline products and launch schedule
          </p>
        </div>
        <UpcomingLaunchesTable />
      </motion.div>
    </div>
  );
};

export default Dashboard;